import { useCart } from 'hooks/context/cart';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { Discount } from 'olo-service';
import { useMemo } from 'react';
import { useInvoiceSummary } from './InvoiceSummary';
import { useOrderDetails } from './OrderDetails';

function fallbackOnUndefined(value: number | undefined, fallbackValue: number) {
  if (value === undefined) return fallbackValue;

  return value;
}

function getDiscountAmount(total: number, discount: Discount) {
  if (discount.amount) return discount.amount / 100;

  return -((discount.percentage || 0) * total / 100);
}

export function useOrderTotals() {
  const { subtotal: cartSubtotal } = useCart();
  const { subtotal: giftCardSubtotal } = useGiftCardCart();
  const { invoiceSummary, setTipvalue } = useInvoiceSummary();
  const { tipValue } = useOrderDetails();

  const tip = useMemo(() => {
    const tipVlue = tipValue || 0;
    setTipvalue?.(tipVlue);

    return tipVlue;
  }, [setTipvalue, tipValue]);
  const subtotal = useMemo(() => fallbackOnUndefined(invoiceSummary?.subtotal, cartSubtotal + giftCardSubtotal), [invoiceSummary, cartSubtotal, giftCardSubtotal]);
  const total = useMemo(() => fallbackOnUndefined(invoiceSummary?.total, subtotal), [invoiceSummary, subtotal]);
  const surcharge = useMemo(() => invoiceSummary?.surchargeAmount || 0, [invoiceSummary]);
  const serviceCharge = useMemo(() => invoiceSummary?.serviceCharge || 0, [invoiceSummary]);
  const surchargeMultiplier = useMemo(() => invoiceSummary?.surchargeAmount ? (1 + invoiceSummary.surchargeAmount) : 1, [invoiceSummary]);
  const totalWithTip = useMemo(() => total, [total]);
  const totalWithSurcharge = useMemo(() => (fallbackOnUndefined(total, subtotal)) * surchargeMultiplier, [total, subtotal, surchargeMultiplier]);
  const discount = useMemo(() => {
    return invoiceSummary?.discounts?.reduce((discountTotal, disc) => discountTotal + getDiscountAmount(subtotal, disc), 0) || 0;
  }, [invoiceSummary, subtotal]);
  const deliveryFee = useMemo(() => invoiceSummary?.deliveryFee || 0, [invoiceSummary]);
  const orderFee = useMemo(() => invoiceSummary?.orderFee ,[invoiceSummary]);
  const giftCard = useMemo(() => invoiceSummary?.giftRedemptionAmount ,[invoiceSummary]);

  return {
    subtotal,
    tax: invoiceSummary?.tax,
    serviceCharge,
    tip,
    discount,
    surcharge,
    surchargeAmount: totalWithTip * surcharge,
    totalWithTip,
    totalWithSurcharge,
    deliveryFee,
    orderFee,
    giftCard
  };
}
